import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { HANDLING_UNIT, HandlingUnitStatusValues, HandlingUnitKindValues, HANDLING_UNIT_STATUS_LABELS, HANDLING_UNIT_KIND_LABELS, } from '../types/handling-unit.js';
import { USER_DATA } from '../types/user.js';
/**
 * Descriptor for HandlingUnit ("Volume") — read via `/pg/v1`.
 * Mutated only by `/stock/handling-units*` (requests 0112 / 0113).
 */
export const handlingUnitDescriptor = {
    name: HANDLING_UNIT,
    label: 'Volume',
    domain: 'stock',
    collectionPath: 'org/{orgId}/handling-unit',
    pathPatternKeys: { orgId: 'string' },
    version: 6,
    description: 'Physical handling unit (volume) — overlay on stock balance; supports split/merge/nest/putaway',
    repositoryConfig: {
        dataSource: DataSourceStrategy.PostgresRest,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: [DataSourceStrategy.PostgresRest],
    writePolicy: 'readonly',
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'code', example: 'V000000042', label: 'Código', type: 'string', required: true },
        { name: 'productId', label: 'Produto', type: 'string', required: false },
        { name: 'variantId', label: 'Variante', type: 'string', required: false },
        { name: 'lotId', label: 'Lote', type: 'string', required: false },
        { name: 'quantity', example: 18.4, label: 'Quantidade', type: 'number', display: 'quantity', required: true },
        { name: 'stockLocationId', label: 'Local', type: 'string', required: true },
        {
            name: 'ownerPartyId',
            label: 'Proprietário',
            type: 'string',
            required: false,
            description: 'FK → Party; null = estoque próprio (0044/0204). Espelha o razão.',
        },
        {
            name: 'entryDate',
            example: '2026-10-22',
            label: 'Data de entrada',
            type: 'date',
            display: 'datetime',
            required: false,
            description: 'Data de negócio da entrada de origem (0204) — nunca createdAt.',
        },
        { name: 'storageAddressId', label: 'Endereço', type: 'string', required: false },
        {
            name: 'status',
            example: 'closed',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...HandlingUnitStatusValues],
            enumLabels: HANDLING_UNIT_STATUS_LABELS,
            enumTones: {
                open: 'success',
                closed: 'neutral',
                consumed: 'neutral',
                shipped: 'neutral',
                blocked: 'destructive',
            },
        },
        {
            name: 'kind',
            example: 'labeled',
            label: 'Tipo',
            type: 'enum',
            required: true,
            enumValues: [...HandlingUnitKindValues],
            enumLabels: HANDLING_UNIT_KIND_LABELS,
        },
        { name: 'parentHandlingUnitId', label: 'Linhagem (pai)', type: 'string', required: false },
        {
            name: 'containerHandlingUnitId',
            label: 'Container',
            type: 'string',
            required: false,
        },
        { name: 'sourceType', label: 'Tipo origem', type: 'string', required: false },
        { name: 'sourceId', label: 'Id origem', type: 'string', required: false },
        {
            name: 'notes',
            example: 'Volume 1/12',
            label: 'Observação',
            type: 'string',
            required: false,
            // Fixed 200 — shorter than FIELD_LIMITS.NOTES (document notes); label-sized (0346).
            maxLength: 200,
            description: 'Observação livre do volume (ex.: Volume n/N gravado na geração).',
        },
        { name: 'idempotencyKey', label: 'Idempotência', type: 'string', required: true },
        { name: 'createdBy', label: 'Criado por', type: 'string', required: false },
        { name: 'createdAt', example: '2026-10-22', label: 'Criado em', type: 'date', display: 'datetime', required: false },
    ],
    foreignKeys: [
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'lotId',
            targetEntity: 'Lot',
            relationship: 'many-to-one',
            displayField: 'lotNumber',
        },
        {
            fieldName: 'stockLocationId',
            targetEntity: 'StockLocation',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'storageAddressId',
            targetEntity: 'StorageAddress',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'parentHandlingUnitId',
            targetEntity: 'HandlingUnit',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'containerHandlingUnitId',
            targetEntity: 'HandlingUnit',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'ownerPartyId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
        },
        // `expand: false` — label-only (0167 §11): resolved by the System API, no join.
        {
            fieldName: 'createdBy',
            targetEntity: USER_DATA,
            relationship: 'many-to-one',
            displayField: 'name',
            expand: false,
        },
    ],
};
//# sourceMappingURL=handling-unit.descriptor.js.map